/// <reference types="@types/google.maps" />

import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  Injector,
  ComponentFactoryResolver,
  ApplicationRef,
  Type,
  NgZone,
} from '@angular/core';
import { TrendencyUtilsService } from '@trendency/utils';
import { TrendencyGoogleMapService } from '@trendency/utils/services/google-map.service';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ExperienceCustomMarkerComponent } from './custom-markers/experience-custom-marker/experience-custom-marker.component';
import { DialogConfig, DialogInjector } from './custom-marker-injector';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import { TourinformMarker, ILatLng } from '../../shared.definitions';

export interface IMarker {
  readonly coordinate: ILatLng;
  readonly component?: Type<any>;
  readonly data?: any;
}

export const DEFAULT_PIN = '/assets/images/mapicons/latnivalo-kereso-pin-single.svg';
export const TOUR_INFOPOINT_PIN = '/assets/images/mapicons/tourinform-infopoint.svg';
export const TOUR_OFFICE_PIN = '/assets/images/mapicons/tourinform-office.svg';

@Component({
  selector: 'app-full-width-map',
  templateUrl: './full-width-map.component.html',
  styleUrls: ['./full-width-map.component.scss'],
})
export class FullWidthMapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('gmap', { static: true }) gmapElement: ElementRef;
  @Input() lat = null;
  @Input() lng = null;
  @Input() mapUIDisabled = true;
  @Input() openFirst = false;
  @Input() fullHeight: boolean;
  @Input() useInCollection = false;

  private _centerLatLng = { lat: 47.513228, lng: 19.053289 };
  @Input() set centerLatLng(latLng: ILatLng) {
    if (latLng) {
      this._centerLatLng = latLng;
      this.centerTo(latLng);
    }
  }

  private readonly markers$ = new BehaviorSubject<TourinformMarker[]>([]);
  private readonly apiReady$ = new BehaviorSubject<boolean>(false);

  _markers: TourinformMarker[];
  @Input() set markers(markers: TourinformMarker[]) {
    this._markers = markers;
    this.markers$.next(markers || []);
  }

  public latLng: any;
  public map: google.maps.Map;
  public options: google.maps.MapOptions;
  public googleMapsUrl: string;
  private readonly destroy$ = new Subject();

  private cluster: MarkerClusterer;
  private readonly clusterOptions = {
    maxZoom: 12,
    styles: [
      {
        textColor: '#FFFFFF',
        textSize: 14,
        url: '/assets/images/mapicons/ellipse.webp',
        scaledSize: typeof google === 'object' ? new google.maps.Size(55, 55) : null,
        height: 55,
        width: 55,
        anchor: [0, 0],
        backgroundSize: 'center',
      },
    ],
  };
  private currentMarkers: google.maps.Marker[] = [];
  private currentlyOpenInfoWindow: any;

  constructor(
    private readonly utilsService: TrendencyUtilsService,
    private readonly gMapService: TrendencyGoogleMapService,
    private readonly injector: Injector,
    private readonly resolver: ComponentFactoryResolver,
    private readonly appRef: ApplicationRef,
    private readonly zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    if (!this.utilsService.isBrowser()) return;

    if (this.lat && this.lng) {
      this.latLng = { lat: +this.lat, lng: +this.lng };
    }

    this.gMapService.initGoogle();

    this.gMapService.googleAPIisReady.pipe(
      takeUntil(this.destroy$)
    ).subscribe((ready) => {
      if (ready) {
        this.apiReady$.next(true);
      }
    });

    combineLatest([this.markers$, this.apiReady$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([markers, apiReady]) => {
        if (apiReady) {
          this.options = {
            disableDefaultUI: this.mapUIDisabled,
            zoom: markers?.length ? 7 : 13,
            center: markers?.length ? this._centerLatLng : this.latLng,
          };
          this.initMap();

          if (markers?.length) {
            this.addMarkers();
            this.openFirstMarker();
          } else if (this.lat && this.lng) {
            this.addSingleMarker(this.latLng);
          }
        }
      });
  }

  private initMap(): void {
    if (this.utilsService.isBrowser()) {
      this.map = new google.maps.Map(this.gmapElement.nativeElement, this.options);

      if (this._markers) {
        this.map.addListener('click', () => {
          if (this.currentlyOpenInfoWindow) {
            this.currentlyOpenInfoWindow.close();
            this.currentlyOpenInfoWindow = null;
          }
        });
        this.cluster = new MarkerClusterer(this.map, [], this.clusterOptions);
      }
    }
  }

  private addSingleMarker(latLng: google.maps.LatLngLiteral): void {
    const image = {
      url: '/assets/images/icons/pin-big.svg',
      size: new google.maps.Size(40, 48),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(20, 48),
    };

    new google.maps.Marker({
      map: this.map,
      position: latLng,
      icon: image,
    });
  }

  private addMarkers(): void {
    if (this.utilsService.isBrowser() && this.map) {
      this.clearMap();

      if (!this.cluster && this._markers?.length) {
        this.cluster = new MarkerClusterer(this.map, [], this.clusterOptions);
      }

      if (this._markers?.length) {
        this._markers.forEach((element) => {
          if (element.component) {
            this.createCustomWindow(element);
          } else {
            this.createSimpleMarker(element);
          }
        });

        if (this.cluster) {
          this.cluster.addMarkers(this.currentMarkers);
        }

        if (this._markers.length === 1) {
          const coordinate = this._markers[0].coordinate;
          if (coordinate) {
            this.map.setCenter(coordinate);
          }
        }
      }
    }
  }

  openFirstMarker(): void {
    if (this.openFirst === true && this._markers && this._markers.length) {
      setTimeout(() => {
        const element = this._markers[0];
        const infoWindow = new google.maps.InfoWindow({ disableAutoPan: true });
        const compRef = this.generateComponent(element.component as Type<any>, element.data);
        const image = this.setPinByType(element.data.type);
        const marker = new google.maps.Marker({
          map: this.map,
          position: element.coordinate,
          icon: image,
        });
        this.zone.run(() => {
          this.onMarkerClick(marker, compRef, infoWindow);
        });
      }, 0);
    }
  }

  clearMap(): void {
    if (this.currentMarkers?.length) {
      this.currentMarkers.forEach((element: any) => {
        if (element?.setMap) {
          element.setMap(null);
        }
      });
      this.currentMarkers = [];
      if (this.cluster?.clearMarkers) {
        this.cluster.clearMarkers();
      }
    }
  }

  createSimpleMarker(markerElement: any): void {
    const image = this.setPinByType(markerElement.data?.type);
    const marker = new google.maps.Marker({
      map: this.map,
      position: markerElement.coordinate,
      icon: image,
    });
    this.currentMarkers.push(marker);
  }

  createCustomWindow(element: any): void {
    const image = this.setPinByType(element.data?.type);
    const marker = new google.maps.Marker({
      map: this.map,
      position: element.coordinate,
      icon: image,
    });
    this.currentMarkers.push(marker);
    const infoWindow = new google.maps.InfoWindow({ disableAutoPan: true });
    const compRef = this.generateComponent(element.component, element.data);
    marker.addListener('click', () => {
      if (this.currentlyOpenInfoWindow) {
        this.currentlyOpenInfoWindow.close();
      }
      const markerPos = marker.getPosition();
      if (!markerPos) {
        return;
      }
      this.panMap(markerPos.lat(), markerPos.lng());
      infoWindow.addListener('closeclick', () => {
        this.appRef.detachView(compRef.hostView);
      });
      this.zone.run(() => {
        this.onMarkerClick(marker, compRef, infoWindow);
      });
      this.currentlyOpenInfoWindow = infoWindow;
    });
  }

  panMap(lat: number, lng: number): void {
    const zoom = this.map.getZoom() ?? 0;
    let zoomModifier = 1;
    if (zoom == 8) zoomModifier = 0.4;
    if (zoom == 9) zoomModifier = 0.25;
    if (zoom == 10) zoomModifier = 0.15;
    if (zoom == 11) zoomModifier = 0.1;
    if (zoom == 12) zoomModifier = 0.05;
    if (zoom == 13) zoomModifier = 0.02;
    if (zoom == 14) zoomModifier = 0.01;
    if (zoom == 15) zoomModifier = 0.005;
    if (zoom == 16) zoomModifier = 0.002;
    if (zoom == 17) zoomModifier = 0.001;
    if (zoom >= 18) zoomModifier = 0.0005;
    this.map.panTo({ lat: lat + zoomModifier, lng: lng });
  }

  generateComponent(component: Type<any>, data?: any): any {
    const compFactory = this.resolver.resolveComponentFactory(component || ExperienceCustomMarkerComponent);

    let compRef;
    if (data) {
      const map = new WeakMap();
      map.set(DialogConfig, { data: data });

      // if (component === CollectionCardComponent) {
      //   compRef = compFactory.create(this.injector);
      //   compRef.instance.card = data.card;
      //   compRef.instance.isMapView = data.isMapView;
      // } else {
        compRef = compFactory.create(new DialogInjector(this.injector, map));
      // }
    } else {
      compRef = compFactory.create(this.injector);
    }

    return compRef;
  }

  onMarkerClick(marker: any, compRef: any, infowindow: any): void {
    this.appRef.attachView(compRef.hostView);
    const div = document.createElement('div');
    div.appendChild(compRef.location.nativeElement);
    infowindow.setContent(div);
    infowindow.open(this.map, marker);

    google.maps.event.addListenerOnce(infowindow, 'domready', () => {
      const iwD = document.querySelector('.gm-style-iw-d') as HTMLElement;
      if (iwD) {
        iwD.style.overflow = 'auto';
      }
    });
  }

  centerTo(latLng: ILatLng): void {
    if (this.map) {
      this.map.panTo(latLng);
    }
  }

  setPinByType(type: any): string {
    if (!type) {
      return DEFAULT_PIN;
    }
    if (type.slug === 'tourinform_office') {
      return TOUR_OFFICE_PIN;
    }
    if (type.slug === 'tourinform_infopoint') {
      return TOUR_INFOPOINT_PIN;
    }
    return DEFAULT_PIN;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
